/** Lux & billing › "Plan & usage": the company's plan, this month's Lux (it renews on the 1st and never rolls
 * over), seats and other usage, and the plans side by side with the way to upgrade. */
import { ArrowUpRight, Gauge, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { useOrg } from "../api/org";
import { useOrgPlan } from "./admin/OrgPlan";
import { BillingPanel } from "./BillingPanel";
import { canUpgrade } from "./Upgrade";
import { Badge, Button, Card, Field, Spinner, useToast } from "../components/ui";
import { useLuxFormat } from "../components/LuxSpend";
import type { MessageKey } from "../i18n/keys";
import { DirIcon } from "../i18n/mirror";
import { allowanceShare, formatValue, ofLimit } from "../lib/plans";
import { landingUrl } from "../config";
import { StatusBadge } from "../components/StatusBadge";
import { MigrationEntry } from "./Migration";

/** The comparison's row labels, company-facing (the catalogue's, written for staff, stay in the admin console). */
const LABELS: Record<(typeof COMPARE)[number], MessageKey> = {
  monthly_price_cents: "lux.plan.item.monthlyPrice",
  annual_monthly_price_cents: "lux.plan.item.annualPrice",
  lux_month: "lux.plan.item.luxMonth",
  lux_reserved_designs: "lux.plan.item.luxReserved",
  lux_price_cents: "lux.plan.item.luxPrice",
  seats: "lux.plan.item.seats",
  seat_price_cents: "lux.plan.item.seatPrice",
  seat_annual_discount_pct: "lux.plan.item.seatDiscount",
  websites: "lux.plan.item.websites",
  service_area_countries: "lux.plan.item.countries",
  powered_by_badge: "lux.plan.item.badge",
  queue_priority: "lux.plan.item.priority",
  audit_log_days: "lux.plan.item.auditLog",
};

/** The rows of the comparison table (the pricing page's headline items). */
const COMPARE = [
  "monthly_price_cents",
  "annual_monthly_price_cents",
  "lux_month",
  "lux_reserved_designs",
  "lux_price_cents",
  "seats",
  "seat_price_cents",
  "seat_annual_discount_pct",
  "websites",
  "service_area_countries",
  "powered_by_badge",
  "queue_priority",
  "audit_log_days",
] as const;

/** "Switching to Solar OS pays" (the switch program): the company names the design or sales software it moves from
 * and Solar OS checks it, or a verified OpenSolar import confirms it by itself (pages/Migration.tsx). Approved: the
 * status badge, the monthly bonus and when the next one comes. */
function SwitchProgram({ orgId, plan, canRequest }: { orgId: string; plan: S["OrganizationPlan"]; canRequest: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const f = useLuxFormat();
  const { t } = f;
  const [competitor, setCompetitor] = useState("");
  const [evidence, setEvidence] = useState("");
  const send = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/orgs/{org_id}/plan/switch-request", {
          params: { path: { org_id: orgId } },
          body: { competitor: competitor.trim(), evidence: evidence.trim() || null },
        }),
      ),
    onSuccess: (data) => {
      qc.setQueryData(["org-plan", orgId, false], data);
      toast(t("lux.switch.sent"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const sw = plan.switch;
  const bonus = (key: string) => plan.catalogue?.plans.find((p) => p.key === key)?.values.switch_bonus_designs;
  const own = plan.values.switch_bonus_designs as number | null | undefined;
  return (
    <div className="plan-switch" id="switch">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <b>{t("lux.switch.title")}</b>
        <span className="row" style={{ gap: 6 }}>
          {sw?.status === "approved" && <StatusBadge badge={plan.badge} />}
          {sw && <Badge tone={sw.status === "approved" ? "ok" : sw.status === "rejected" ? "danger" : "warn"}>{t("lux.switch.status", { status: sw.status })}</Badge>}
        </span>
      </div>
      <p className="muted small" style={{ margin: "4px 0 8px" }}>
        {t("lux.switch.intro", {
          free: Number(bonus("free") ?? 3),
          pro: Number(bonus("pro") ?? 10),
          business: Number(bonus("business") ?? 25),
          months: Number(plan.values.switch_bonus_expiry_months ?? 12),
        })}
        {sw?.status === "approved" && own ? (
          <>
            {" "}
            <b data-testid="switch-next-bonus">{t("lux.switch.next", { amount: own, date: f.date(plan.usage.resets_at) })}</b>
            {plan.usage.lux_bonus_designs ? ` ${t("lux.switch.left", { amount: f.lux(plan.usage.lux_bonus_designs) })}` : ""}
          </>
        ) : null}
        {sw && (
          <>
            {" "}
            <FormattedMessage
              id="lux.switch.request"
              values={{ b: (c) => <b dir="auto">{c}</b>, competitor: sw.competitor, note: sw.decision_note || "none" }}
            />
          </>
        )}
      </p>
      <MigrationEntry compact />
      {canRequest && sw?.status !== "approved" && (
        <div className="form-grid" style={{ marginTop: 10 }}>
          <Field label={t("lux.switch.from")}>
            <input className="input" list="switch-sources" maxLength={100} value={competitor} onChange={(e) => setCompetitor(e.target.value)} placeholder={t("lux.switch.fromPlaceholder")} dir="auto" />
            <datalist id="switch-sources">
              {(plan.catalogue?.switch_sources ?? []).map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </Field>
          <Field label={t("lux.switch.evidence")}>
            <input className="input" maxLength={2000} value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder={t("lux.switch.evidencePlaceholder")} dir="auto" />
          </Field>
          <div>
            <Button disabled={!competitor.trim()} loading={send.isPending} onClick={() => send.mutate()}>
              {sw ? t("lux.switch.again") : t("lux.switch.request.button")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function PlanUsage({ orgId }: { orgId: string }) {
  const plan = useOrgPlan(orgId, false);
  const { can } = useOrg();
  const canRequest = can("credits_billing", "create");
  const f = useLuxFormat();
  const { t } = f;
  if (!plan.data) return <Card title={t("lux.plan.title")}>{plan.isLoading ? <Spinner /> : null}</Card>;
  const d: S["OrganizationPlan"] = plan.data;
  const u = d.usage;
  const items = d.catalogue?.items ?? [];
  const plans = d.catalogue?.plans ?? [];
  const granted = u.lux_reserved_granted + u.lux_free_granted;
  const left = Math.max(u.lux_reserved_left, 0) + Math.max(u.lux_free_left, 0);
  const share = allowanceShare(granted - left, granted);
  const label = (key: (typeof COMPARE)[number]) => t(LABELS[key]);
  const fmt = (key: string, value: unknown) => {
    const item = items.find((i) => i.key === key);
    return item ? formatValue(item, value, f) : String(value);
  };
  return (
    <Card
      title={
        <span className="row" style={{ gap: 8 }}>
          <Gauge size={18} /> {t("lux.plan.title")} <Badge tone="grad">{d.plan_name}</Badge>
          <StatusBadge badge={d.badge} />
        </span>
      }
      description={t("lux.plan.description")}
      actions={
        canUpgrade(d) && (
          <Link className="btn btn-primary" to="/lux#upgrade">
            <Sparkles size={15} /> {d.plan === "free" ? t("lux.spend.upgrade") : t("lux.plan.upgradeBusiness")}
          </Link>
        )
      }
    >
      <div className="stack">
        <div>
          <div className="row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
            <b>{t("lux.plan.used", { used: f.lux(granted - left), granted: f.lux(granted) })}</b>
            <span className="muted small">{t("lux.plan.renews", { date: f.date(u.resets_at) })}</span>
          </div>
          <div
            className={`usage-bar${share >= 1 ? " is-full" : ""}`}
            role="progressbar"
            aria-label={t("lux.plan.usedAria")}
            aria-valuemin={0}
            aria-valuemax={granted}
            aria-valuenow={granted - left}
            aria-valuetext={t("lux.plan.used", { used: f.lux(granted - left), granted: f.lux(granted) })}
          >
            <span style={{ width: `${Math.round(share * 100)}%` }} />
          </div>
          <div className="muted small" style={{ marginTop: 6 }}>
            {t("lux.plan.breakdown", {
              reserved: f.lux(u.lux_reserved_left),
              free: f.lux(u.lux_free_left),
              bonus: u.lux_bonus_designs ? f.lux(u.lux_bonus_designs) : "none",
              purchased: f.lux(u.lux_purchased),
              designs: u.designs_this_month,
            })}
          </div>
        </div>
        <div className="grid-4 stats-tight">
          <div className="card stat">
            <div className="label">{t("lux.plan.users")}</div>
            <div className="value">{ofLimit(u.seats_used, u.seats, "contract", f)}</div>
            <div className="sub muted small">{t("lux.plan.viewers", { viewers: u.viewers, luxSeats: u.lux_seats ?? 0 })}</div>
          </div>
          <div className="card stat">
            <div className="label">{t("lux.plan.websites")}</div>
            <div className="value">{ofLimit(u.websites, d.values.websites as number | null, undefined, f)}</div>
          </div>
          <div className="card stat">
            <div className="label">{t("lux.plan.widgetSearches")}</div>
            <div className="value">{ofLimit(u.widget_searches_month, d.values.widget_searches_month as number | null, undefined, f)}</div>
            <div className="sub muted small">{t("lux.plan.searchesThen", { price: f.lux(10), block: 25 })}</div>
          </div>
          <div className="card stat">
            <div className="label">{t("lux.plan.designerSearches")}</div>
            <div className="value">{ofLimit(u.designer_searches_month, d.values.designer_searches_month as number | null, undefined, f)}</div>
            <div className="sub muted small">{t("lux.plan.searchesThen", { price: f.lux(10), block: 25 })}</div>
          </div>
        </div>
        <details className="plan-compare-details">
          <summary>{t("lux.plan.compareAll")}</summary>
        <div className="table-wrap">
          <table className="table plan-compare">
            <thead>
              <tr>
                <th>{t("lux.plan.compare")}</th>
                {plans.map((p) => (
                  <th key={p.key} className={p.key === d.plan ? "current" : undefined}>
                    {p.key === d.plan ? t("lux.plan.yours", { plan: p.name }) : p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((key) => (
                <tr key={key}>
                  <td>{label(key)}</td>
                  {plans.map((p) => (
                    <td key={p.key} className={p.key === d.plan ? "current" : undefined}>
                      {p.key === d.plan ? fmt(key, d.values[key]) : fmt(key, p.values[key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </details>
        <BillingPanel orgId={orgId} canBuy={canRequest} />
        <SwitchProgram orgId={orgId} plan={d} canRequest={canRequest} />
        <div className="row" style={{ justifyContent: "space-between" }}>
          <span className="muted small">{t("lux.plan.everyPlan")}</span>
          <a className="btn btn-ghost" href={`${landingUrl()}/#pricing`}>
            {t("lux.plan.website")}{" "}
            <DirIcon>
              <ArrowUpRight size={15} />
            </DirIcon>
          </a>
        </div>
      </div>
    </Card>
  );
}
